import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { createElement } from 'react';
import { usePressFeedbackClick } from '../hooks/usePressFeedbackClick.js';
import { cx } from '../utils/cx.js';
import { Loader } from './Loader.js';
import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondaryPill' | 'tertiaryPill' | 'ghost' | 'danger' | 'soft';

/** Default: native `<button>`. Pass `as={Link}` (etc.) — `to`/`href` come from host props. */
export type ButtonProps<T extends ElementType = 'button'> = {
  as?: T;
  variant?: ButtonVariant;
  /** Compact sizes for tables and toolbars */
  size?: 'xs' | 'sm' | 'md';
  children: ReactNode;
  /** Shows spinner, disables control, sets aria-busy. */
  loading?: boolean;
  /** Only used when `as` is omitted (native `<button>`). */
  type?: HTMLButtonElement['type'];
  /**
   * When false, skip `data-wp-pressing` visual hold (click is always sync).
   * Use for toolbar toggles where the press flash feels flickery.
   */
  pressFeedback?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'type'>;

export function Button<T extends ElementType = 'button'>(props: ButtonProps<T>) {
  const {
    as,
    variant = 'primary',
    size = 'md',
    className,
    type = 'button',
    children,
    loading = false,
    disabled,
    onClick,
    pressFeedback = true,
    ...rest
  } = props;
  const Comp = (as ?? 'button') as ElementType;
  const isNativeButton = !as;
  const resolvedType = type ?? 'button';
  const { onClick: pressOnClick, pressAttrs } = usePressFeedbackClick(
    pressFeedback ? onClick : undefined,
    {
      disabled: Boolean(disabled || loading),
      deferHostDefault: !isNativeButton,
      submitForm: isNativeButton && resolvedType === 'submit',
    },
  );

  return createElement(
    Comp,
    {
      ...(isNativeButton ? { type: resolvedType } : {}),
      className: cx(
        styles.btn,
        styles[variant],
        styles[`sz_${size}`],
        styles.focus,
        loading && styles.loading,
        className,
      ),
      disabled: disabled || loading,
      'aria-busy': loading || undefined,
      onClick: pressFeedback ? pressOnClick : onClick,
      ...(pressFeedback ? pressAttrs : {}),
      ...rest,
    } as Record<string, unknown>,
    <span className={styles.face} aria-hidden />,
    loading ? (
      <span className={styles.spinner} aria-hidden>
        <Loader
          size="sm"
          tone={variant === 'primary' ? 'onBrand' : variant === 'danger' ? 'onDanger' : 'default'}
        />
      </span>
    ) : null,
    <span className={cx(styles.label, loading && styles.labelHidden)}>{children}</span>,
  );
}
